import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../../contexts/authContext';
import { authClient } from '../../../../lib/connect/clients';
// The authenticated user's tenant identity (full name + nickname) from the
// Connect /me surface — shared _system content carries a {{company}}
// placeholder that renders as this alias. Keyed by the session's tenant id
// and cached indefinitely (identity changes admin-side, not mid-session);
// keying by tenant means a same-tab account switch resolves a fresh entry
// instead of serving the previous tenant's alias from cache.
export function useTenantIdentity() {
    const { user } = useAuth();
    const tenantId = user?.tenant_id;
    const { data, isLoading } = useQuery({
        queryKey: [
            'tenant-identity',
            tenantId
        ],
        queryFn: async ()=>(await authClient.getMe({})).tenant ?? null,
        // getMe resolves the tenant from the session, not from tenant_id — that
        // field is optional on the auth user and used here only as the cache key.
        // Gate on the user being present, not on a field the RPC ignores, or a
        // login without tenant_id would never fire and read the neutral fallback
        // forever with no error.
        enabled: !!user,
        staleTime: Number.POSITIVE_INFINITY,
        // Fail fast to the neutral fallback rather than stall a caller: a failing
        // GetMe (e.g. thousand-sunny deployed ahead of sabaody's route) must not
        // hold identity-dependent UI through the default retry backoff.
        retry: false
    });
    const identity = data ? {
        name: data.name,
        nickname: data.nickname,
        alias: data.nickname || data.name
    } : null;
    return {
        identity,
        isLoading
    };
}
/**
 * Substitutes the shared-content {{company}} placeholder with the tenant
 * alias. Falls back to the neutral phrase "your company" when the alias is
 * missing (identity still loading, or a tenant with no name/nickname) so a raw
 * {{company}} token never reaches a user. This is the render-time backstop the
 * server relies on: CreateAuditFromTemplate (pangaea
 * delivery_aggregate_repository.go) resolves {{company}} at clone time but
 * deliberately LEAVES it intact when the tenant row is missing, trusting this
 * resolver to finish the job — so the "your company" literal lives only here.
 * Uses a replacer FUNCTION, not a string
 * replacement: a string replacement interprets `$&`/`$1`/`$'` sequences, so a
 * nickname like "A$&P" would corrupt the output (and `$&` would re-emit the
 * very token this exists to remove) — a function passes the alias through
 * verbatim, matching the server's literal strings.ReplaceAll.
 */ export function resolveCompanyPlaceholder(text, alias) {
    const replacement = alias?.trim() ? alias : 'your company';
    return text.replace(/\{\{company\}\}/g, ()=>replacement);
}
